import { useState } from 'react';
import { Loader2, UserPlus, X } from 'lucide-react';
import type { Participant } from 'shared';
import { ApiConflictError, ApiValidationError } from '@/lib/api';
import { useCreateParticipant, type ParticipantCreateInput } from '@/hooks/useParticipants';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { FormField } from './activities/ActivityUi';

interface CreateParticipantModalProps {
  /** What staff had typed in a search, split into nome and apelido. */
  initialName?: string;
  /** The centre the form opens on, e.g. the session's. */
  defaultCenterId?: number | null;
  onClose: () => void;
  onCreated: (participant: Participant) => void;
}

interface FormState {
  nome: string;
  apelido: string;
  genero: string;
  dataNascimento: string;
  telemovel: string;
  email: string;
  numeroCc: string;
  autorizacaoImagem: boolean;
  autorizacaoEe: boolean;
  digitalCenterId: number | null;
}

/** Splits "Maria da Silva" into nome "Maria" and apelido "da Silva". */
function splitName(full: string): { nome: string; apelido: string } {
  const words = full.trim().split(/\s+/).filter(Boolean);
  return { nome: words[0] ?? '', apelido: words.slice(1).join(' ') };
}

/** Whole years between a YYYY-MM-DD birth date and today; null if unset. */
function ageOn(dataNascimento: string, today: Date = new Date()): number | null {
  const birth = new Date(`${dataNascimento}T00:00:00`);
  if (!dataNascimento || Number.isNaN(birth.getTime())) return null;
  const hadBirthday =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  return today.getFullYear() - birth.getFullYear() - (hadBirthday ? 0 : 1);
}

function blankToNull(value: string): string | null {
  const t = value.trim();
  return t === '' ? null : t;
}

function toInput(form: FormState, minor: boolean): ParticipantCreateInput {
  return {
    nome: form.nome.trim(),
    apelido: form.apelido.trim(),
    genero: form.genero,
    dataNascimento: form.dataNascimento,
    telemovel: blankToNull(form.telemovel),
    email: blankToNull(form.email),
    numeroCc: blankToNull(form.numeroCc),
    autorizacaoImagem: form.autorizacaoImagem,
    autorizacaoEe: minor && form.autorizacaoEe,
    digitalCenterId: form.digitalCenterId,
  };
}

/**
 * Registers by hand someone who cannot sign up in the PWA, usually for want
 * of a smartphone. No contact is asked for: they may have neither a phone nor
 * an email. They start active, so they can be added to activities at once.
 */
export default function CreateParticipantModal({
  initialName = '',
  defaultCenterId = null,
  onClose,
  onCreated,
}: CreateParticipantModalProps) {
  const create = useCreateParticipant();
  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];

  const [form, setForm] = useState<FormState>(() => ({
    ...splitName(initialName),
    genero: '',
    dataNascimento: '',
    telemovel: '',
    email: '',
    numeroCc: '',
    autorizacaoImagem: false,
    autorizacaoEe: false,
    digitalCenterId: defaultCenterId,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const age = ageOn(form.dataNascimento);
  // A minor needs the guardian's consent, recorded as a tick as on Utilizadores.
  const minor = age !== null && age < 18;

  const set =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setChecked =
    (key: 'autorizacaoImagem' | 'autorizacaoEe') => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.checked }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setFormError(null);
    try {
      const created = await create.mutateAsync(toInput(form, minor));
      onCreated(created);
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setErrors(err.errors);
      } else if (err instanceof ApiConflictError) {
        setErrors({ email: 'Já existe um participante com este email.' });
      } else {
        setFormError('Não foi possível registar o participante. Tenta de novo.');
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-participant-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 id="create-participant-title" className="text-lg font-semibold text-foreground">
            Registar participante
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          id="create-participant-form"
          onSubmit={handleSubmit}
          className="overflow-y-auto flex-1 px-6 py-5 space-y-5"
        >
          <p className="text-sm text-muted-foreground">
            Para quem não se consegue registar na aplicação, por exemplo por não ter smartphone.
            Fica activo e pode ser logo adicionado às atividades.
          </p>

          {formError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md" role="alert">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Nome" htmlFor="cp-nome" required error={errors.nome}>
              <input id="cp-nome" className="input" value={form.nome} onChange={set('nome')} required autoFocus />
            </FormField>
            <FormField label="Apelido" htmlFor="cp-apelido" required error={errors.apelido}>
              <input id="cp-apelido" className="input" value={form.apelido} onChange={set('apelido')} required />
            </FormField>
            <FormField label="Data de nascimento" htmlFor="cp-nascimento" required error={errors.dataNascimento}>
              <input
                id="cp-nascimento"
                type="date"
                className="input"
                value={form.dataNascimento}
                onChange={set('dataNascimento')}
                required
              />
            </FormField>
            <FormField label="Género" htmlFor="cp-genero" required error={errors.genero}>
              <select id="cp-genero" className="input" value={form.genero} onChange={set('genero')} required>
                <option value="" disabled>
                  Escolher…
                </option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="I">Indeterminado</option>
              </select>
            </FormField>
            <FormField label="Telemóvel" htmlFor="cp-telemovel" error={errors.telemovel} hint="Opcional">
              <input id="cp-telemovel" type="tel" className="input" value={form.telemovel} onChange={set('telemovel')} />
            </FormField>
            <FormField label="Email" htmlFor="cp-email" error={errors.email} hint="Opcional">
              <input id="cp-email" type="email" className="input" value={form.email} onChange={set('email')} />
            </FormField>
            <FormField label="Número CC" htmlFor="cp-cc" error={errors.numeroCc} hint="Opcional">
              <input id="cp-cc" className="input" value={form.numeroCc} onChange={set('numeroCc')} />
            </FormField>
            <FormField label="Centro habitual" htmlFor="cp-centro" error={errors.digitalCenterId}>
              <select
                id="cp-centro"
                className="input"
                value={form.digitalCenterId ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    digitalCenterId: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              >
                <option value="">Sem centro</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.centerName}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={form.autorizacaoImagem}
                onChange={setChecked('autorizacaoImagem')}
                className="w-4 h-4 accent-primary"
              />
              Autorização de Imagem
            </label>
            {minor && (
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.autorizacaoEe}
                  onChange={setChecked('autorizacaoEe')}
                  className="w-4 h-4 accent-primary"
                />
                Autorização EE
              </label>
            )}
          </div>
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancelar
          </button>
          <button
            type="submit"
            form="create-participant-form"
            disabled={create.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Registar
          </button>
        </div>
      </div>
    </div>
  );
}
